import { create } from 'zustand'
import { LocationRegionHierarchy, User } from '@/client/types/user'
import { useClientStore } from '@stores/client'
import { useCollectionsStore } from '@stores/engagement/collections'

// Conditional import for SSR compatibility - using dynamic import for ES modules

// Storage keys
const USER_DATA_KEY = 'user_data'
const USER_DATA_TIMESTAMP_KEY = 'user_data_timestamp'

// Configuration - Aligned with server session (24h) but conservative for data freshness
const TTL = 2 * 60 * 60 * 1000 // 2 hours - balance between UX and data freshness
const SYNC_INTERVAL = 90 * 60 * 1000 // 90 minutes (sync before TTL expires)
const MAX_RETRY_ATTEMPTS = 3
const RETRY_DELAY = 2000 // 2 seconds

interface UserState {
  user: User | null
  preferences: UserPreference[]
  regions: LocationRegionHierarchy | null
  isLoadingUser: boolean
  isLoadingPreferences: boolean
  isUpdatingPreference: number | boolean
  error: Error | null
  verifyEmail: boolean
  verifyPhone: boolean
  initialized: boolean
  refreshUser: (callback?: () => void) => Promise<void>
  refreshCachedUser: () => Promise<User | null>
  setUser: (user: User | null, opts?: { skipPersist?: boolean }) => void
  setPreference: (uaptid: number, checked: boolean) => Promise<void>
  getRegions: (lid?: number) => Promise<LocationRegionHierarchy>
  clearUser: () => void
  initialize: () => Promise<void>
  allowCriticalAction: (fn: () => void) => void
  dismissACModals: () => void
  cleanup: () => void
  logout: () => Promise<void>
}

// Session Manager - handles sync timer and storage
class SessionManager {
  private syncTimer: NodeJS.Timeout | null = null
  private leaderPingTimer: ReturnType<typeof setInterval> | null = null
  private isLeader: boolean = false
  private storageChannel: BroadcastChannel | null = null
  private retryCount: number = 0
  private isOnline: boolean = true
  private tabId: string = Math.random().toString(36).substring(2, 15)

  // Initialize storage lazily - will be set after module loads
  store: any = undefined
  
  constructor() {
    // Initialize storage
    if (typeof window !== 'undefined') {
      import('all-localstorage').then((mod) => {
        const Storage = mod.default || mod;
        if (Storage) {
          this.store = new Storage({
            prefix: 'ssui',
            encrypt: true,
            token: '8jlaigaHJISRYduh8yqZb5weNtJO6whQa4'
          });
        }
      }).catch(() => {
        this.store = undefined;
      });
    }
    
    // Initialize broadcast channel for cross-tab communication
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.storageChannel = new BroadcastChannel('user_session_sync')
      this.setupBroadcastListeners()
    }

    // Monitor online/offline status
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline)
      window.addEventListener('offline', this.handleOffline)
      this.isOnline = navigator.onLine
    }

    // Try to become leader
    this.tryBecomeLeader()
  }

  private handleOnline = () => {
    console.log('[SESSION] Back online, resuming sync')
    this.isOnline = true
    this.retryCount = 0
  }

  private handleOffline = () => {
    console.log('[SESSION] Offline detected, pausing sync')
    this.isOnline = false
  }

  private setupBroadcastListeners() {
    if (!this.storageChannel) return

    this.storageChannel.addEventListener('message', (event) => {
      // Ignore messages from this tab to prevent infinite loops
      if (event.data.senderId === this.tabId) {
        return
      }

      if (event.data.type === 'USER_UPDATED') {
        // Another tab updated user data, refresh local state (skip persist to avoid broadcast loop).
        // Take the user from the message, not storage: all-localstorage's codec
        // corrupts some characters, so a large user record can read back as a
        // string, and setting that as the user signed every other tab out.
        const userData = event.data.user
        if (userData && typeof userData === 'object') {
          useUserStore.getState().setUser(userData, { skipPersist: true })
        }
      } else if (event.data.type === 'SESSION_EXPIRED') {
        // Another tab detected session expiration
        console.log('[SESSION] Session expired in another tab')
        this.handleSessionExpired()
      } else if (event.data.type === 'LEADER_PING') {
        // Leader is active, step down if we tried to become leader
        this.isLeader = false
      }
    })
  }

  private tryBecomeLeader() {
    if( !this.store ) return

    // Simple leader election: first tab to check becomes leader
    const leaderTimestamp = this.store.get('ssui_leader_ts')
    const now = Date.now()

    if (!leaderTimestamp || now - parseInt(leaderTimestamp) > 5000) {
      // No leader or leader hasn't pinged in 5 seconds
      this.isLeader = true
      this.store.set('ssui_leader_ts', now.toString())
      console.log('[SESSION] Became session leader')
      
      this.leaderPingTimer = setInterval(() => {
        if (this.isLeader) {
          this.store.set('ssui_leader_ts', Date.now().toString())
          this.broadcastMessage({ type: 'LEADER_PING' })
        }
      }, 3000)
    }
  }

  private broadcastMessage(message: any) {
    if (this.storageChannel) {
      this.storageChannel.postMessage({ ...message, senderId: this.tabId })
    }
  }

  storeUser(user: User) {
    if( !this.store ) return

    try {
      this.store.set(USER_DATA_KEY, user)
      this.store.set(USER_DATA_TIMESTAMP_KEY, Date.now().toString())
      
      // Notify other tabs
      this.broadcastMessage({ type: 'USER_UPDATED', user })
    } catch (error) {
      console.error('[SESSION] Failed to store user data:', error)
    }
  }

  getStoredUser(): User | null {
    if( !this.store ) return null
    try {
      const userData = this.store.get(USER_DATA_KEY)
      // A corrupted read comes back as a string; never hand that out as a user.
      return userData && typeof userData === 'object' ? userData : null
    } catch (error) {
      console.error('[SESSION] Failed to retrieve user data:', error)
      return null
    }
  }

  isExpired(): boolean {
    if( !this.store ) return false

    try {
      const timestamp = this.store.get(USER_DATA_TIMESTAMP_KEY)
      if (!timestamp) return true
      
      const storedTime = parseInt(timestamp)
      return Date.now() - storedTime > TTL
    } catch (error) {
      console.error('[SESSION] Failed to check expiration:', error)
      return true
    }
  }

  clearStorage() {
    if( !this.store ) return

    try {
      this.store.clear(USER_DATA_KEY)
      this.store.clear(USER_DATA_TIMESTAMP_KEY)
    } catch (error) {
      console.error('[SESSION] Failed to clear storage:', error)
    }
  }

  startSync(onSync: () => Promise<void>, onExpire: () => void) {
    // Only the leader tab should run sync
    if (!this.isLeader) {
      console.log('[SESSION] Not leader, skipping sync timer setup')
      return
    }

    this.stopSync() // Clear any existing timer

    const performSync = async () => {
      // Don't sync if offline
      if (!this.isOnline) {
        console.log('[SESSION] Offline, skipping sync')
        this.scheduleSyncRetry(performSync)
        return
      }

      try {
        console.log('[SESSION] Running background sync...')
        await onSync()
        this.retryCount = 0 // Reset retry count on success
        
        // Schedule next sync
        this.syncTimer = setTimeout(performSync, SYNC_INTERVAL)
      } catch (error: any) {
        console.error('[SESSION] Sync failed:', error)
        
        // Check if it's a session expiration (401)
        if (error.status === 401 || error.message?.includes('Session expired')) {
          console.log('[SESSION] Session expired during sync')
          this.handleSessionExpired()
          onExpire()
        } else {
          // Network error or other issue - retry with backoff
          this.retryCount++
          if (this.retryCount <= MAX_RETRY_ATTEMPTS) {
            console.log(`[SESSION] Retrying sync (${this.retryCount}/${MAX_RETRY_ATTEMPTS})`)
            this.scheduleSyncRetry(performSync)
          } else {
            console.error('[SESSION] Max retry attempts reached, stopping sync')
            this.retryCount = 0
            // Schedule next regular sync anyway
            this.syncTimer = setTimeout(performSync, SYNC_INTERVAL)
          }
        }
      }
    }

    // Start the sync cycle
    this.syncTimer = setTimeout(performSync, SYNC_INTERVAL)
    console.log('[SESSION] Sync timer started')
  }

  private scheduleSyncRetry(performSync: () => Promise<void>) {
    const delay = RETRY_DELAY * Math.pow(2, this.retryCount - 1) // Exponential backoff
    this.syncTimer = setTimeout(performSync, delay)
  }

  private handleSessionExpired() {
    this.clearStorage()
    this.broadcastMessage({ type: 'SESSION_EXPIRED' })
  }

  stopSync() {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer)
      this.syncTimer = null
      console.log('[SESSION] Sync timer stopped')
    }
  }

  cleanup() {
    this.stopSync()

    if (this.leaderPingTimer) {
      clearInterval(this.leaderPingTimer)
      this.leaderPingTimer = null
    }
    
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline)
      window.removeEventListener('offline', this.handleOffline)
    }

    if (this.storageChannel) {
      this.storageChannel.close()
      this.storageChannel = null
    }

    if (this.store && this.isLeader) {
      this.store.clear()
    }
  }
}

// Create singleton session manager
const sessionManager = new SessionManager()

export const useUserStore = create<UserState>((set, get) => {
  // Function to fetch user data with enhanced error handling
  const fetchUserData = async (skipCache: boolean = false, silent: boolean = false): Promise<User | null> => {
    const state = get()
    if (!skipCache && state.isLoadingUser) return null

    try {
      // A silent refresh must not flip isLoadingUser — RestrictedPage treats that
      // as "session unknown" and replaces the whole console with a spinner.
      if (!silent) set({ isLoadingUser: true, error: null })
      
      // Use server-side cache by default; only request a background refresh after
      // mutations (skipCache=true). Avoid use_cache=0 — it bypasses the server
      // cache entirely and hits the DB on every call, causing timeouts under load.
      const query = skipCache ? 'refresh_cache=1' : ''
      console.log('[USER STORE] fetching /api/admin/users/info...', { skipCache, timestamp: new Date().toISOString() })
      const response = await useClientStore.getState().request<APIResponse<User>>(`/api/admin/users/info${query ? `?${query}` : ''}`, 'GET', undefined, { timeout: 30000 })
      
      console.log('[USER STORE] /users/info response:', {
        status: response.status,
        hasData: !!response.data,
        dataType: typeof response.data,
        userName: response.data?.preferred_name || response.data?.username || '(none)',
        message: (response as any)?.message,
      })

      // Handle 401 - User not authenticated or session expired
      if (response.status === 401) {
        console.warn('[USER STORE] 401 — session not found or expired. The login session cookie may not have been set/sent correctly.')
        sessionManager.clearStorage()
        set({
          user: null,
          isLoadingUser: false,
          error: null,
          initialized: true
        })
        return null
      }

      // Handle other error status codes
      if (response.status !== 200) {
        const error = new Error(`Failed to fetch user: ${response.status}`)
        ;(error as any).status = response.status
        throw error
      }

      // Success - store user data with TTL
      const userData = response.data
      sessionManager.storeUser(userData)
      
      console.log('[USER STORE] user loaded successfully:', {
        username: userData?.username,
        email: userData?.email1 ? '***@***' : '(none)',
        authid: userData?.authid ? userData.authid.substring(0, 8) + '...' : '(none)',
      })

      set({
        user: userData,
        isLoadingUser: false,
        error: null,
        initialized: true
      })

      return userData
    } catch (error: any) {
      console.error('[USER STORE] Request error:', error)

      const isTimeout = error.status === 408 || error.code === 'AbortError' || error.message?.includes('timeout')
      const isSessionExpired = !isTimeout && (error.status === 401 || error.message?.includes('Session expired'))

      if (isSessionExpired) {
        sessionManager.clearStorage()
        set({
          user: null,
          isLoadingUser: false,
          error: null,
          initialized: true
        })
      } else {
        // Network error, timeout, or other transient issue — don't clear the user
        const errorObj = error instanceof Error ? error : new Error('Failed to fetch user')
        set({
          isLoadingUser: false,
          error: isTimeout ? null : errorObj,
          initialized: true
        })
      }

      return null
    }
  }

  const fetchUserPreferences = async () => {
    set({ isLoadingPreferences: true })
    try {
      const response = await useClientStore.getState().request<APIResponse<UserPreference[]>>(`/api/admin/v2/user/preferences`)
      
      if (response.status === 401) {
        // Session expired while fetching preferences
        throw new Error('Session expired')
      }
      
      if (response.status > 201) {
        throw new Error('Failed to fetch user preferences')
      }

      set({
        preferences: response.data,
        isLoadingPreferences: false
      })
    } catch (error: any) {
      const errorObj = error instanceof Error ? error : new Error('Failed to fetch user preferences')
      set({ isLoadingPreferences: false })
      console.error('[USER STORE] Preferences error:', errorObj)
    }
  }

  const fetchRegions = async ( lid = 14 ) => { // Ghana
    try {
      const storeId = `user-regions:${lid}`

      // Use cached list
      const regions = sessionManager.store.get( storeId )
      if( regions ){
        set({ regions })
        return regions
      }
      
      // Fetch fresh location regions
      const response = await useClientStore.getState().request<APIResponse<LocationRegionHierarchy>>(`/api/admin/v2/location/${lid}/hierarchy?include_inactive=false&ancestor_depths=1&descendant_depths=1`)
      if( response.status > 201 )
        throw new Error('Failed fetching user\'s regions')

      sessionManager.store.set( storeId, response.data )
      set({ regions: response.data })
      return response.data;
    }
    catch( error ){ 
      console.log('Request error  --', error );
      throw error;
    }
  }

  const clearSession = async () => {
    try { await useClientStore.getState().request<APIResponse<string>>('/api/admin/auth/user/account/logout', 'POST', {}) }
    catch (error) { console.error('Logout failed:', error) }
  }

  // Background sync function
  const performBackgroundSync = async () => {
    console.log('[USER STORE] Performing background sync...')
    const userData = await fetchUserData(true) // Skip cache, force fresh fetch
    
    if (userData) {
      // Fetch additional data in parallel
      await Promise.all([
        fetchUserPreferences(),
        fetchRegions(),
        useCollectionsStore.getState().fetchCollections()
      ]).catch(error => {
        console.warn('[USER STORE] Additional data fetch failed:', error)
      })
    }
  }

  let sessionExpiredHandled = false;
  let logoutInFlight = false;

  // Handle session expiration
  const handleSessionExpired = () => {
    if (sessionExpiredHandled) return;
    sessionExpiredHandled = true;

    console.log('[USER STORE] Session expired, logging out...');
    get().clearUser()
    
    // Notify messaging to disconnect gracefully
    if (typeof window !== 'undefined') {
      try {
        import('@/client/stores/messaging/connection').then((mod) => {
          const { useMessagingConnectionStore } = mod;
          useMessagingConnectionStore.getState().handleSessionExpired();
        }).catch((error) => {
          console.warn('[USER STORE] Could not notify messaging of session expiry:', error);
        });
      } catch (error) {
        console.warn('[USER STORE] Could not notify messaging of session expiry:', error);
      }
    }
  }

  return {
    user: null,
    regions: null,
    preferences: [],
    isLoadingUser: false,
    isLoadingPreferences: false,
    isUpdatingPreference: false,
    error: null,
    verifyEmail: false,
    verifyPhone: false,
    initialized: false,

    initialize: async () => {
      const state = get()
      if (state.initialized) return

      const clientStore = useClientStore.getState()
      
      console.log('[USER STORE] Initializing...')
      
      // Wait for client store to be initialized first
      if (!clientStore.initialized) {
        console.log('[USER STORE] Waiting for client store initialization...')
        set({ initialized: true })
        return
      }

      // Check if we have cached user data that's still valid
      const cachedUser = sessionManager.getStoredUser()
      const isExpired = sessionManager.isExpired()

      if (cachedUser && !isExpired) {
        console.log('[USER STORE] Using cached user data, verifying session...')
        set({
          user: cachedUser,
          initialized: true,
          isLoadingUser: false
        })

        // Verify the server session is still valid before starting background fetches
        const freshUser = await fetchUserData()

        if (!freshUser) {
          // Server session is dead — the cached user is stale
          console.log('[USER STORE] Server session expired, clearing cached user')
          return
        }

        // Session is valid — start background sync and fetch additional data
        sessionManager.startSync(performBackgroundSync, handleSessionExpired)

        Promise.all([
          fetchUserPreferences(),
          fetchRegions(),
          // useCollectionsStore.getState().fetchCollections()
        ]).catch(error => {
          console.warn('[USER STORE] Background preferences fetch failed:', error)
        })

        return
      }

      // No cache or expired - fetch fresh data
      console.log('[USER STORE] No valid cache, fetching fresh data...')
      set({ initialized: true })

      const userData = await fetchUserData()
      
      if (userData) {
        // Start sync timer after successful initial fetch
        sessionManager.startSync(performBackgroundSync, handleSessionExpired)

        // Fetch additional data
        await Promise.all([
          fetchUserPreferences(),
          fetchRegions(),
          // useCollectionsStore.getState().fetchCollections()
        ]).catch(error => {
          console.warn('[USER STORE] Additional data fetch failed:', error)
        })
      }
    },

    refreshUser: async (callback) => {
      console.log('[USER STORE] refreshUser called — waiting for cookie to settle...')
      // Give the browser time to store the session cookie from the login response
      await new Promise(r => setTimeout(r, 500))
      console.log('[USER STORE] refreshUser — fetching fresh user data...')

      let userData = await fetchUserData(true)

      // If the first attempt returns null (401 or timeout), retry once.
      // This handles: (a) transient MongoDB session-lookup latency causing an
      // empty session, and (b) staging backend slowness causing a timeout.
      if (!userData) {
        console.warn('[USER STORE] refreshUser — first attempt failed, retrying in 2s...')
        await new Promise(r => setTimeout(r, 2000))
        userData = await fetchUserData(true)
      }

      console.log('[USER STORE] refreshUser result:', {
        gotUser: !!userData,
        userName: userData?.preferred_name || userData?.username || '(none)',
      })

      if (userData) {
        await Promise.all([
          fetchUserPreferences(),
          fetchRegions(),
          // useCollectionsStore.getState().fetchCollections()
        ]).catch(error => {
          console.warn('[USER STORE] Refresh additional data failed:', error)
        })
      }

      typeof callback === 'function' && callback()
    },

    refreshCachedUser: () => fetchUserData(true, true),

    setUser: (user: User | null, opts?: { skipPersist?: boolean }) => {
      set({ user })
      
      // Update cache when user is set (skip when syncing from another tab to avoid broadcast loop)
      if (user && !opts?.skipPersist) {
        sessionManager.storeUser(user)
      }
    },

    setPreference: async (uaptid: number, checked: boolean) => {
      set({ isUpdatingPreference: uaptid })
      try {
        const update = { preference: !checked ? 0 : 1 }
        const response = await useClientStore.getState().request<APIResponse<UserPreference[]>>(
          `/api/admin/v2/user/preference/${uaptid}/set`,
          'POST',
          update
        )

        if (response.status === 401) {
          throw new Error('Session expired')
        }

        if (response.status > 201) {
          throw new Error('Failed updating user preferences')
        }
        
        set({
          preferences: get().preferences.map(pref => 
            pref.uaptid === uaptid ? { ...pref, ...update } : pref
          ),
          isUpdatingPreference: false
        })

        // Update cached user's preferences if needed
        const currentUser = get().user
        if (currentUser) {
          sessionManager.storeUser(currentUser)
        }
      } catch (error: any) {
        const errorObj = error instanceof Error ? error : new Error('Failed updating user preference')
        set({ isUpdatingPreference: false })
        
        console.error('[USER STORE] Preference update error:', errorObj)

        // Handle session expiration
        if (error.status === 401 || error.message?.includes('Session expired')) {
          handleSessionExpired()
        }
      }
    },

    getRegions: async ( lid = 14 ) => {
      return await fetchRegions( lid )
    },

    clearUser: () => {
      set({ user: null, preferences: [], initialized: false })

      sessionManager.clearStorage()
      sessionManager.stopSync()
    },

    allowCriticalAction: (fn: () => void) => {
      const state = get()
      
      if (state.user && state.user.email1 && !state.user.email1_ver) {
        set({ verifyEmail: true })
        return
      }
      
      if (state.user && state.user.phone1 && !state.user.phone1_ver) {
        set({ verifyPhone: true })
        return
      }

      typeof fn === 'function' && fn()
    },

    dismissACModals: () => {
      set({ verifyEmail: false, verifyPhone: false })
    },

    cleanup: () => {
      sessionManager.cleanup()
    },

    logout: async () => {
      if (logoutInFlight) return
      logoutInFlight = true

      const { clearUser, cleanup } = get()

      try {
        await clearSession()
      } finally {
        clearUser()
        cleanup()
        window.location.replace('/')
      }
    }
  }
})

// Cleanup on page unload - use pagehide instead of beforeunload for bfcache compatibility
if (typeof window !== 'undefined') {
  // beforeunload blocks bfcache - use pagehide instead
  window.addEventListener('pagehide', () => {
    useUserStore.getState().cleanup()
  })
}